import React, { useMemo } from "react";

import {
  flatMap,
  map,
  some,
  uniq,
  filter,
  groupBy,
  mapValues,
  size,
  identity,
} from "lodash/fp";
import { shallow } from "zustand/shallow";

import FilterChip from "@app/analytics/components/FilterChip";
import { FILTER_CHIPS } from "@app/analytics/constants/filterChips";
import useSearchMeetingsStore from "@app/analytics/hooks/meetings/useSearchMeetingsStore";
import type {
  FilterChipKind,
  FilterChipType,
  SearchResult,
} from "@app/analytics/types";

import { Row } from "@rui/Stack/Stack";

// Takes search results and extracts a unique set of search category matches
// then maps them to the filter chips and sums the number of results for each
const extractFilterKindsFromSearchResults = (searchResults: SearchResult[]) => {
  // get list of all unique search categories per search result
  // ex [["action_item", "key_question"], ["action_item", "note"]]
  const uniqueSearchCategories = map(
    (searchResult) => uniq(map("category", searchResult.matches)),
    searchResults
  );

  // map each category list to a list of filter chip kinds and flatten
  // ex [["action_item", "key_question"], ["action_item", "note"]] =>
  // [["highlight", "action_item", "key_question"], ["action_item", "note"]] =>
  // ["highlight", "action_item", "key_question", "action_item", "note"]
  const filterChipKinds: FilterChipKind[] = flatMap(
    (searchCategoryList) =>
      map(
        "kind",
        filter(
          (filterChip) =>
            some(
              (searchCategory) =>
                filterChip.categories.includes(searchCategory),
              searchCategoryList
            ),
          FILTER_CHIPS
        )
      ),
    uniqueSearchCategories
  );

  // finally sum up each kind
  // { action_item: 2, key_question: 1, highlight: 3, note: 1 }
  const filterChipCounts = mapValues(size, groupBy(identity, filterChipKinds));
  filterChipCounts["all"] = searchResults.length;

  return filterChipCounts;
};

const SearchFilters = () => {
  const { allSearchResults, setSearchFilterChipKinds, searchFilterChipKinds } =
    useSearchMeetingsStore(
      (state) => ({
        allSearchResults: state.allSearchResults,
        searchFilterChipKinds: state.searchFilterChipKinds,
        setSearchFilterChipKinds: state.setSearchFilterChipKinds,
      }),
      shallow
    );

  const filterKindCounts = useMemo(
    () => extractFilterKindsFromSearchResults(allSearchResults),
    [allSearchResults]
  );

  const handleChipClick = (filterChip: FilterChipType) => {
    if (filterChip.kind === "all") {
      const allChip = new Set<FilterChipKind>(["all"]);
      setSearchFilterChipKinds(allChip);
      return;
    }
    const newSelectedChips = new Set<FilterChipKind>(searchFilterChipKinds);
    newSelectedChips.delete("all");
    if (searchFilterChipKinds.has(filterChip.kind)) {
      newSelectedChips.delete(filterChip.kind);
    } else {
      newSelectedChips.add(filterChip.kind);
    }

    if (newSelectedChips.size === 0) {
      newSelectedChips.add("all");
    }

    setSearchFilterChipKinds(newSelectedChips);
  };

  return (
    <Row
      variant="left"
      sx={{ py: 2, flexWrap: "wrap", rowGap: "8px", columnGap: "8px" }}
    >
      {FILTER_CHIPS.map((filterChip) =>
        filterKindCounts[filterChip.kind] || filterChip.kind === "all" ? (
          <FilterChip
            key={filterChip.kind}
            label={filterChip.label}
            numResults={filterKindCounts[filterChip.kind] ?? 0}
            onClick={() => handleChipClick(filterChip)}
            isSelected={searchFilterChipKinds.has(filterChip.kind)}
          />
        ) : null
      )}
    </Row>
  );
};
export default SearchFilters;
